import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    LabelList,
    ResponsiveContainer,
    Cell,
    CartesianGrid,
} from 'recharts';
import { getSelfcareScoreTier, roundHalfUp } from '../../pages/selfcare/selfcareReportLogic';

const getColor = (value) => getSelfcareScoreTier(value).color;

const extractDimensionCode = (value) => {
    const match = String(value || '').toUpperCase().match(/D[1-7]/);
    return match ? match[0] : String(value || '');
};

const getDimensionOrder = (value) => {
    const match = String(value || '').toUpperCase().match(/D([1-7])/);
    return match ? Number(match[1]) : 999;
};

const renderValueLabel = (props) => {
    const { x, y, width, height, value } = props;
    const v = Number(value);

    const cx = (x || 0) + (width || 0);
    const cy = (y || 0) + (height || 0) / 2;

    return (
        <text
            x={cx + 8}
            y={cy}
            fill="#334155"
            fontSize={10}
            fontWeight={800}
            dominantBaseline="middle"
            textAnchor="start"
        >
            {`${Number.isFinite(v) ? roundHalfUp(v, 2) : 0}`}
        </text>
    );
};

const ResumeBarChartSelfcare = ({ data, yAxisWidth = 180 }) => {
    if (!data) return null;

    const rawItems = Array.isArray(data)
        ? data.map((dimension, index) => [dimension?.key || dimension?.code || index, dimension])
        : Object.entries(data);

    const parsedData = rawItems.map(([id, dimension]) => {
        const value = Number.isFinite(Number(dimension.average)) ? Number(dimension.average) : 0;
        const code = extractDimensionCode(dimension.code || dimension.key || dimension.title || id);
        return {
            id,
            name: code,
            code,
            fullName: dimension.reportTitle || dimension.title || code,
            value,
            color: getColor(value),
        };
    }).sort((a, b) => getDimensionOrder(a.code) - getDimensionOrder(b.code));

    const height = Math.max(220, parsedData.length * 26 + 20);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const value = payload[0].value;
            return (
                <div style={{ backgroundColor: 'white', padding: 10, border: '1px solid #ccc' }}>
                    <div style={{ fontWeight: 700 }}>{label}</div>
                    <div style={{ fontSize: 11, color: '#5b5b5b' }}>{payload[0]?.payload?.fullName}</div>
                    <div style={{ fontWeight: 600, color: '#3E246B' }}>{roundHalfUp(value, 2)}%</div>
                </div>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={parsedData} layout="vertical" margin={{ top: 22, right: 36, bottom: 0, left: 10 }}>
                <CartesianGrid stroke="#e8e8e8" vertical horizontal={false} />

                <XAxis
                    type="number"
                    domain={[0, 100]}
                    ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                    tick={{ fontSize: 10, fill: '#8c8c8c' }}
                    tickFormatter={(v) => `${v}%`}
                    axisLine={false}
                    tickLine={false}
                    orientation="top"
                />
                <YAxis
                    type="category"
                    dataKey="name"
                    width={yAxisWidth}
                    interval={0}
                    tick={{ fontSize: 10, fill: '#5b5b5b' }}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />

                <Bar dataKey="value" barSize={12} radius={[0, 8, 8, 0]} isAnimationActive={false}>
                    {parsedData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                        dataKey="value"
                        content={renderValueLabel}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default ResumeBarChartSelfcare;
